import { SagaIterator } from 'redux-saga';
import { createAction, PrepareAction, PayloadActionCreator } from '@reduxjs/toolkit';
import { call, cancelled } from 'redux-saga/effects';
import isObject from 'lodash/isObject';
import { RequestError } from 'types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Saga<Args extends any[] = any[]> = (...args: Args) => any;

type CanceledResult = {
  error?: { content: Error };
  cancelled: true;
};
type ErrorResult<Error> = {
  error: { content: Error };
  cancelled: false;
};
type SuccessResult<Data> = {
  data: Data;
  error: undefined;
  cancelled: false;
};

type OnCompleteResult<Data, Error> = CanceledResult | ErrorResult<Error> | SuccessResult<Data>;

type OnComplete<Data, Error = RequestError> = (result: OnCompleteResult<Data, Error>) => void;

interface ActionWithCallback<Data, Error> {
  meta?: { onComplete?: OnComplete<Data, Error> };
}

type IsValidArg<T> = T extends object | string | number | undefined ? true : false;

type PrepareActionWithCallback<PA, Callback> = PA extends (a: infer A, b: infer B, c: infer C, d: infer D) => infer R
  ? IsValidArg<D> extends true
    ? (a: A, b: B, c: C, d: D, onComplete?: Callback) => R
    : IsValidArg<C> extends true
    ? (a: A, b: B, c: C, onComplete?: Callback) => R
    : IsValidArg<B> extends true
    ? (a: A, b: B, onComplete?: Callback) => R
    : IsValidArg<A> extends true
    ? (a: A, onComplete?: Callback) => R
    : (onComplete?: Callback) => R
  : never;

type AugmentedAction<PA, Data, Error> = PrepareActionWithCallback<PA, OnComplete<Data, Error>>;

// TODO: refacto into single function with all generics when https://github.com/Microsoft/TypeScript/pull/26349 fixed
export function createActionWithCallback<Data, Error>() {
  return <PA extends PrepareAction<unknown>, T extends string = string>(
    type: T,
    prepareAction?: PA
  ): PayloadActionCreator<ReturnType<PA>['payload'], T, AugmentedAction<PA, Data, Error>> => {
    const augmentedPrepareAction = (...actionArgs: unknown[]) => {
      const preparedAction = prepareAction?.(...actionArgs);
      const sanitizedPreparedAction = isObject(preparedAction) ? preparedAction : {};
      const lastArg = actionArgs[actionArgs.length - 1];
      const callback = typeof lastArg === 'function' ? lastArg : undefined;
      if (!callback) {
        return sanitizedPreparedAction;
      }
      const actionBag = { meta: {}, ...sanitizedPreparedAction };
      return {
        ...actionBag,
        meta: { ...actionBag.meta, onComplete: callback },
      };
    };
    return createAction(type, augmentedPrepareAction as AugmentedAction<PA, Data, Error>);
  };
}

function onComplete<Data, Error>(action: unknown, result: OnCompleteResult<Data, Error>) {
  const actionTyped = action as ActionWithCallback<Data, Error>;
  if (actionTyped.meta?.onComplete) {
    actionTyped.meta.onComplete(result);
  }
}

export function sagaWithCallback(saga: Saga): Saga {
  return function* callbackedSaga(action: unknown): SagaIterator {
    let data;
    try {
      data = yield call(saga, action);
    } catch (error) {
      return onComplete(action, {
        error: { content: error },
        cancelled: false,
      });
    }

    if (yield cancelled()) {
      return onComplete(action, { cancelled: true });
    }

    return onComplete(action, {
      data,
      cancelled: false,
      error: undefined,
    });
  };
}
